import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Sparkles,
  Download,
  Clock3,
  Gift,
  Megaphone,
  ChevronRight,
  Bot,
  Mic,
  Send,
} from 'lucide-react';
import { ResponsiveContainer, CartesianGrid, XAxis, YAxis, Tooltip, LineChart, Line } from 'recharts';
import useStore from '@/store/useStore';
import { useUiLanguage } from '@/lib/i18n';
import { apiMerchantCampaigns, apiMerchantDashboard } from '@/services/api';
import { AI_ACTION_TYPES, executeAIAction, pickPrimaryInsight } from '@/lib/aiActionEngine';
import QrCodeModal from '@/components/merchant/QrCodeModal';
import CenteredStatusPopup from '@/components/merchant/CenteredStatusPopup';

const RANGE_OPTIONS = [
  { label: '7 days', value: 7 },
  { label: '30 days', value: 30 },
  { label: '90 days', value: 90 },
];

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

function downloadCsv(filename, rows) {
  const body = rows.map((r) => r.map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([body], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { merchantUser } = useStore();
  const { t } = useUiLanguage();
  const [data, setData] = useState(null);
  const [showQR, setShowQR] = useState(false);
  const [rangeDays, setRangeDays] = useState(30);
  const [insightIndex, setInsightIndex] = useState(0);
  const [actionBusyId, setActionBusyId] = useState('');
  const [actionToast, setActionToast] = useState('');
  const [campaignCounts, setCampaignCounts] = useState({ all: 0, completed: 0 });
  const [campaignAudienceCounts, setCampaignAudienceCounts] = useState({});
  const [assistantQuery, setAssistantQuery] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [dashboard, campaignData] = await Promise.all([
        apiMerchantDashboard(rangeDays),
        apiMerchantCampaigns('all').catch(() => null),
      ]);
      setData(dashboard);
      if (campaignData?.counts && typeof campaignData.counts === 'object') {
        setCampaignCounts(campaignData.counts);
      }
      if (campaignData?.audience_counts && typeof campaignData.audience_counts === 'object') {
        setCampaignAudienceCounts(campaignData.audience_counts);
      }
    } catch (e) {}
  }, [rangeDays]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const assistantName = 'Flyback Restaurant Advisor';
  const rangeLabel = RANGE_OPTIONS.find((r) => r.value === rangeDays)?.label || '30 days';

  const statsSeries = useMemo(() => {
    const all = Array.isArray(data?.daily_stats) ? data.daily_stats : [];
    return all.slice(-Math.min(rangeDays, all.length || 1));
  }, [data?.daily_stats, rangeDays]);

  const metrics = data?.metrics || {};
  const totalRevenue = Number(metrics.total_revenue || 0);
  const revenueByPoints = Number(metrics.revenue_by_points || 0);
  const revenueWithoutPoints = Number(metrics.revenue_without_points || 0);
  const active7 = Number(metrics.active_customers_last_7 || 0);
  const active30 = Number(metrics.active_customers_last_30 || 0);
  const enrolledUsers = Number(metrics.enrolled_users || 0);
  const totalGuestsEstimate = Number(metrics.total_guests_estimate || 0);
  const avgOrderValue = Number(data?.avg_order_value || 0);
  const topRedeemedItemName = String(metrics.top_redeemed_item_name || 'Truffle Fries');
  const topRedeemedCount = Number(metrics.top_redeemed_item_redemptions || metrics.campaign_redemptions || 0);
  const topRedeemedPoints = Number(metrics.top_redeemed_item_points || 500);

  const atRiskCount = Math.max(Number(data?.total_customers || 0) - Number(metrics.active_customers_last_30 || 0), 0);
  const lowEngagementCount = Math.max(Number(data?.total_customers || 0) - Number(metrics.active_customers_last_7 || 0), 0);
  const vipAudienceCount = Math.max(Number(campaignAudienceCounts?.high_frequency_reward || 0), 0);
  const aiActionCandidates = useMemo(
    () => [
      {
        id: 'risk-win-back',
        type: 'risk',
        priority_score: atRiskCount * 2,
        value: atRiskCount,
        title: `${atRiskCount} ${t("customers haven't visited in 14+ days")}`,
        actionLabel: t('Run Win-back Campaign'),
        actionType: AI_ACTION_TYPES.RUN_CAMPAIGN,
        context: { segment: 'AT_RISK', campaign_subtype: 'win_back', audience_count: atRiskCount },
      },
      {
        id: 'boost-visits',
        type: 'opportunity',
        priority_score: lowEngagementCount,
        value: lowEngagementCount,
        title: `${lowEngagementCount} ${t('customers need a visit boost this week')}`,
        actionLabel: t('Boost Visits'),
        actionType: AI_ACTION_TYPES.BOOST_VISITS,
        context: { segment: 'ALL', campaign_subtype: 'visit_booster', audience_count: lowEngagementCount },
      },
      {
        id: 'reward-vips',
        type: 'opportunity',
        priority_score: vipAudienceCount,
        value: vipAudienceCount,
        title: `${vipAudienceCount} ${t('VIP users are eligible for reward campaign')}`,
        actionLabel: t('Reward Users'),
        actionType: AI_ACTION_TYPES.REWARD_USERS,
        context: { segment: 'ACTIVE', campaign_subtype: 'high_frequency_reward', audience_count: vipAudienceCount },
      },
    ],
    [atRiskCount, lowEngagementCount, t, vipAudienceCount]
  );
  const primaryInsight = useMemo(() => pickPrimaryInsight(aiActionCandidates), [aiActionCandidates]);
  const rotatingInsights = useMemo(() => {
    const secondary = aiActionCandidates.filter((item) => item.id !== primaryInsight?.id).slice(0, 3);
    return [
      ...secondary.map((item) => ({
        ...item,
        text: item.title,
        navigates: false,
      })),
      {
        id: 'ai-followup',
        text: t('Need deeper details? Open Loyalty AI for breakdown.'),
        actionLabel: t('Open Loyalty AI'),
        navigates: true,
      },
    ];
  }, [aiActionCandidates, primaryInsight?.id, t]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setInsightIndex((prev) => (prev + 1) % Math.max(rotatingInsights.length, 1));
    }, 3500);
    return () => window.clearInterval(timer);
  }, [rotatingInsights.length]);

  const runAction = useCallback(async (item) => {
    if (!item?.actionType) return;
    setActionBusyId(item.id || '');
    try {
      const res = await executeAIAction(item.actionType, item.context || {});
      setActionToast(`✅ ${res.message}`);
      window.setTimeout(() => setActionToast(''), 3800);
      fetchData();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setActionToast(`❌ ${typeof detail === 'string' ? detail : t('Unable to execute AI action right now.')}`);
      window.setTimeout(() => setActionToast(''), 3800);
    }
    setActionBusyId('');
  }, [fetchData, t]);

  const activeInsight = rotatingInsights[insightIndex] || rotatingInsights[0];

  const chartData = useMemo(() => {
    const totalVisits = Math.max(Number(metrics.total_visits || 0), 1);
    return statsSeries.map((s) => {
      const dayRevenue = Number(s.revenue || 0);
      const dayVisits = Number(s.visits || 0);
      const dayPointsRevenue = (dayVisits / totalVisits) * revenueByPoints;
      return {
        label: s.day,
        date: s.date,
        revenue: Number(dayRevenue.toFixed(2)),
        pointsRevenue: Number(dayPointsRevenue.toFixed(2)),
        nonPointsRevenue: Number(Math.max(dayRevenue - dayPointsRevenue, 0).toFixed(2)),
        visits: dayVisits,
      };
    });
  }, [metrics.total_visits, revenueByPoints, statsSeries]);

  const aovTrendData = useMemo(() => {
    return statsSeries.map((s) => {
      const visits = Number(s.visits || 0);
      const revenue = Number(s.revenue || 0);
      const aov = visits > 0 ? revenue / visits : 0;
      return {
        date: s.date,
        label: s.day,
        aov: Number(aov.toFixed(2)),
        baseline: Number(avgOrderValue.toFixed(2)),
      };
    });
  }, [avgOrderValue, statsSeries]);

  const repeatVisitLift = useMemo(() => {
    if (!statsSeries.length) return 0;
    const split = Math.max(1, Math.floor(statsSeries.length / 2));
    const older = statsSeries.slice(0, split).reduce((sum, row) => sum + Number(row.visits || 0), 0);
    const recent = statsSeries.slice(split).reduce((sum, row) => sum + Number(row.visits || 0), 0);
    if (older <= 0) return 0;
    return Number((((recent - older) / older) * 100).toFixed(1));
  }, [statsSeries]);

  const assistantActions = useMemo(
    () => [
      {
        id: 'assistant-remind-inactive',
        label: t('Send reminder to inactive customers'),
        actionType: AI_ACTION_TYPES.RUN_CAMPAIGN,
        context: {
          campaign_subtype: 'win_back',
          segment: 'AT_RISK',
          audience_count: atRiskCount,
        },
      },
      {
        id: 'assistant-boost-second-visit',
        label: t('Boost second visit this week'),
        actionType: AI_ACTION_TYPES.BOOST_VISITS,
        context: {
          campaign_subtype: 'visit_booster',
          segment: 'RISK',
          audience_count: lowEngagementCount,
        },
      },
      {
        id: 'assistant-reward-vips',
        label: t('Reward VIP members'),
        actionType: AI_ACTION_TYPES.REWARD_USERS,
        context: {
          campaign_subtype: 'high_frequency_reward',
          segment: 'ACTIVE',
          audience_count: vipAudienceCount,
        },
      },
    ],
    [atRiskCount, lowEngagementCount, t, vipAudienceCount]
  );

  const handleExport = () => {
    const rows = [
      ['Metric', 'Value'],
      ['Range', rangeLabel],
      ['New Enrolments', enrolledUsers],
      ['Estimated Guests', totalGuestsEstimate],
      ['Total Revenue', totalRevenue.toFixed(2)],
      ['Revenue by Points', revenueByPoints.toFixed(2)],
      ['Revenue without Points', revenueWithoutPoints.toFixed(2)],
      ['Active Customers (7d)', active7],
      ['Active Customers (30d)', active30],
      ['Visit Frequency', Number(metrics.visit_frequency || 0).toFixed(2)],
      ['Campaign New Users', Number(metrics.campaign_new_users_generated || 0)],
      ['Campaign Revenue Impact', Number(metrics.campaign_revenue_impact || 0).toFixed(2)],
      ['Redemption Rate %', Number(metrics.redemption_rate || 0).toFixed(1)],
      ['Average Spend / Visit', Number(metrics.avg_spend_per_visit || 0).toFixed(2)],
      [],
      ['Date', 'Day', 'Visits', 'Revenue', 'Points Revenue', 'Non-Points Revenue'],
      ...chartData.map((row) => [row.date, row.label, row.visits, row.revenue, row.pointsRevenue, row.nonPointsRevenue]),
    ];
    downloadCsv(`flyback-dashboard-${rangeDays}d.csv`, rows);
  };

  const handleAssistantSubmit = () => {
    const query = String(assistantQuery || '').trim();
    if (!query) return;
    const token = `${Date.now()}`;
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem('flyback_ai_pending_query', JSON.stringify({ query, token }));
    }
    setAssistantQuery('');
    navigate('/merchant/ai', {
      state: {
        pendingQuery: query,
        pendingQueryToken: token,
        autoSubmit: true,
      },
    });
  };

  return (
    <div className="space-y-5" data-testid="merchant-dashboard">
      <CenteredStatusPopup message={actionToast} />
      <style>{`
        @keyframes flybackAiBorderFlow {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes flybackAiBorderGlow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(255,255,255,0.18), 0 0 18px 0 rgba(255,255,255,0.08); }
          50% { box-shadow: 0 0 0 1px rgba(255,255,255,0.35), 0 0 26px 1px rgba(255,255,255,0.22); }
        }
      `}</style>
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-[#0f172a]" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>
            {t('Hello')}, {merchantUser?.name?.split(' ')[0] || t('Merchant')}
          </h1>
          <p className="text-sm text-stone-500">{t('Dynamic performance overview for the last')} {rangeLabel}.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="h-10 rounded-xl border border-stone-200 bg-white p-1 flex items-center gap-1">
            {RANGE_OPTIONS.map((item) => (
              <button
                key={item.value}
                type="button"
                onClick={() => setRangeDays(item.value)}
                className={`h-8 px-3 rounded-lg text-sm font-semibold ${rangeDays === item.value ? 'bg-brand-soft text-brand' : 'text-stone-600 hover:bg-stone-50'}`}
              >
                {t(item.label)}
              </button>
            ))}
          </div>
          <button onClick={handleExport} className="h-10 px-4 rounded-xl border border-stone-200 bg-white text-sm font-semibold flex items-center gap-2">
            <Download className="w-4 h-4" /> {t('Export CSV')}
          </button>
          <button onClick={() => setShowQR(true)} className="h-10 px-4 rounded-xl gradient-brand-btn text-sm font-semibold">{t('Show QR')}</button>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-2xl p-4 text-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg"
        style={{ background: 'linear-gradient(135deg, rgba(var(--brand-rgb),0.96), rgba(var(--brand-rgb),0.74))' }}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-full bg-white/20 flex items-center justify-center"><Sparkles className="w-4 h-4" /></div>
            <div>
              <div className="flex items-center gap-2.5">
                <p className="font-bold text-lg">{primaryInsight?.title || t('No priority action detected right now.')}</p>
                <button
                  type="button"
                  onClick={() => runAction(primaryInsight)}
                  disabled={!primaryInsight?.actionType || !!actionBusyId}
                  className="h-7 px-2.5 rounded-md bg-white text-brand text-xs font-semibold inline-flex items-center gap-1 shrink-0"
                >
                  {actionBusyId === primaryInsight?.id ? <Clock3 className="w-3.5 h-3.5 animate-spin" /> : null}
                  {primaryInsight?.actionLabel || t('Run Campaign')} <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
          <button onClick={() => navigate('/merchant/ai')} className="h-9 px-4 rounded-lg bg-white text-brand text-sm font-semibold shrink-0">{t('Ask AI')}</button>
        </div>

        <div className="mt-3 relative overflow-hidden rounded-xl p-[1px]">
          <div
            className="pointer-events-none absolute inset-0 rounded-xl opacity-95"
            style={{
              background: 'linear-gradient(110deg, rgba(255,255,255,0.14), rgba(255,255,255,0.62), rgba(255,255,255,0.16), rgba(255,255,255,0.52), rgba(255,255,255,0.14))',
              backgroundSize: '260% 260%',
              animation: 'flybackAiBorderFlow 3.2s ease-in-out infinite, flybackAiBorderGlow 2.2s ease-in-out infinite',
            }}
          />
          <div className="relative overflow-hidden rounded-[11px] border border-white/20 bg-white/10 px-3 py-2">
            <div className="absolute inset-0 opacity-25 bg-gradient-to-r from-transparent via-white/60 to-transparent animate-[pulse_2.8s_ease-in-out_infinite]" />
            <AnimatePresence mode="wait">
              <motion.div
                key={activeInsight?.id}
                initial={{ opacity: 0, y: 10, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -8, scale: 0.99 }}
                transition={{ duration: 0.35, ease: 'easeOut' }}
                className="relative flex items-center justify-between gap-2"
              >
                <p className="text-xs font-semibold text-white">{activeInsight?.text}</p>
                <button
                  type="button"
                  onClick={() => {
                    if (activeInsight?.navigates) {
                      navigate('/merchant/ai');
                      return;
                    }
                    runAction(activeInsight);
                  }}
                  disabled={activeInsight?.navigates ? false : actionBusyId === activeInsight?.id}
                  className="h-7 px-2.5 rounded-md bg-white text-brand text-xs font-semibold inline-flex items-center gap-1 shrink-0"
                >
                  {actionBusyId === activeInsight?.id ? <Clock3 className="w-3.5 h-3.5 animate-spin" /> : null}
                  {activeInsight?.actionLabel || t('Know More')} <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        <div className="rounded-2xl border border-stone-200 bg-white p-5 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md hover:border-emerald-200 hover:bg-emerald-50/60">
          <p className="text-sm text-stone-500">{t('Repeat Visit Lift')}</p>
          <p className="mt-2 text-5xl font-bold tracking-tight text-[#0f172a]" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>
            {Number.isFinite(repeatVisitLift) ? `${repeatVisitLift > 0 ? '+' : ''}${repeatVisitLift}%` : '0%'}
          </p>
          <p className="mt-1 text-xs text-stone-500">{t('Customers returning more often')}</p>
        </div>
        <div className="rounded-2xl border border-stone-200 bg-white p-5 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md hover:border-sky-200 hover:bg-sky-50/60">
          <p className="text-sm text-stone-500">{t('Revenue from Loyalty')}</p>
          <p className="mt-2 text-5xl font-bold tracking-tight text-[#0f172a]" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>
            {fmtMoney(revenueByPoints)}
          </p>
          <p className="mt-1 text-xs text-stone-500">{t('Total earned from loyalty members in selected range')}</p>
        </div>
        <div className="rounded-2xl border border-stone-200 bg-white p-5 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md hover:border-cyan-200 hover:bg-cyan-50/60">
          <p className="text-sm text-stone-500">{t('AOV (Average Order Value)')}</p>
          <p className="mt-2 text-5xl font-bold tracking-tight text-[#0f172a]" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>
            {fmtMoney(avgOrderValue)}
          </p>
          <p className="mt-1 text-xs text-stone-500">{t('Average order value for loyalty visits')}</p>
        </div>
        <div className="rounded-2xl border border-stone-200 bg-white p-5 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md hover:border-amber-200 hover:bg-amber-50/60">
          <p className="text-sm text-stone-500">{t('Campaign Metrics')}</p>
          <div className="mt-2 space-y-1.5 text-sm">
            <p className="flex items-center justify-between gap-3">
              <span className="text-stone-500">{t('Total Campaigns sent')}</span>
              <span className="font-semibold text-stone-800">{Number(campaignCounts?.all || 0)}</span>
            </p>
            <p className="flex items-center justify-between gap-3">
              <span className="text-stone-500">{t('Redemptions')}</span>
              <span className="font-semibold text-stone-800">{Number(metrics.campaign_redemptions || 0)}</span>
            </p>
            <p className="flex items-center justify-between gap-3">
              <span className="text-stone-500">{t('New users')}</span>
              <span className="font-semibold text-stone-800">{Number(metrics.campaign_new_users_generated || 0)}</span>
            </p>
            <p className="flex items-center justify-between gap-3">
              <span className="text-stone-500">{t('Revenue impact')}</span>
              <span className="font-semibold text-brand">{fmtMoney(metrics.campaign_revenue_impact || 0)}</span>
            </p>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-stone-200 bg-white p-5">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-full bg-brand-soft text-brand flex items-center justify-center"><Bot className="w-4 h-4" /></div>
          <p className="text-2xl font-bold tracking-tight text-brand" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>
            {t('Your Personal AI Assistant')}
          </p>
        </div>
        <p className="text-sm text-stone-600 mt-2">
          {t('Today you made')} {fmtMoney(totalRevenue)}. {t('You gained')} {enrolledUsers} {t('new customers.')}{' '}
          {t('Active members in last 30 days')}: {active30}.
        </p>
        <p className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold mt-3">{t('Quick actions')}</p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {assistantActions.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => runAction(item)}
              disabled={actionBusyId === item.id}
              className="h-8 px-3 rounded-lg border border-brand/25 bg-brand-soft text-brand text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-55"
            >
              {actionBusyId === item.id ? <Clock3 className="w-3.5 h-3.5 animate-spin" /> : null}
              {item.label}
            </button>
          ))}
        </div>
        <div className="mt-3 h-10 rounded-xl border border-brand/30 bg-white pl-3 pr-1 flex items-center gap-2">
          <input
            value={assistantQuery}
            onChange={(e) => setAssistantQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAssistantSubmit();
              }
            }}
            placeholder={t('Ask me anything...')}
            className="flex-1 bg-transparent text-sm text-stone-500 outline-none"
          />
          <button type="button" className="h-8 w-8 rounded-md border border-stone-200 bg-stone-50 text-stone-500 inline-flex items-center justify-center"><Mic className="w-3.5 h-3.5" /></button>
          <button type="button" onClick={handleAssistantSubmit} className="h-8 w-8 rounded-md bg-brand text-white inline-flex items-center justify-center"><Send className="w-3.5 h-3.5" /></button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1.8fr,1fr] gap-4">
        <div className="rounded-2xl border border-stone-200 bg-white p-5 transition-all duration-300 hover:bg-brand-soft/20 hover:-translate-y-0.5 hover:shadow-md">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-2xl font-bold text-[#0f172a]" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>{t('AOV Trend')}</p>
              <p className="text-xs text-stone-500">{t('Average order value movement for selected range')}</p>
            </div>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={aovTrendData}>
                <CartesianGrid stroke="#f1f5f9" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11 }}
                  tickFormatter={(value) => {
                    const parsed = new Date(value);
                    if (Number.isNaN(parsed.getTime())) return value;
                    if (rangeDays <= 14) return parsed.toLocaleDateString(undefined, { weekday: 'short' });
                    return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
                  }}
                  interval={Math.max(Math.floor(aovTrendData.length / 12), 0)}
                />
                <YAxis tick={{ fontSize: 11 }} domain={[0, 'auto']} />
                <Tooltip
                  labelFormatter={(value) => {
                    const parsed = new Date(value);
                    if (Number.isNaN(parsed.getTime())) return value;
                    return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
                  }}
                  formatter={(v, name) => [`$${Number(v || 0).toFixed(2)}`, name === 'aov' ? t('AOV') : t('Baseline')]}
                />
                <Line type="monotone" dataKey="baseline" stroke="#9ca3af" strokeWidth={2} strokeDasharray="4 4" dot={false} />
                <Line type="monotone" dataKey="aov" stroke="var(--brand-solid)" strokeWidth={3} dot={{ r: 2 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="space-y-4">
          <div className="rounded-3xl border border-stone-200 bg-[#f7f7f6] p-5 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md">
            <div className="flex items-center gap-2">
              <span className="h-7 w-7 rounded-full bg-white border border-orange-100 flex items-center justify-center">
                <Clock3 className="w-3.5 h-3.5 text-orange-400" />
              </span>
              <div>
                <p className="text-xs font-semibold text-stone-700">{t('Peak Dining Hour')}</p>
                <p className="text-xs text-stone-400">{t('Based on loyalty check-ins')}</p>
              </div>
            </div>
            <p className="mt-4 text-xl leading-none font-bold text-[#0f172a]" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>
              7:00 PM
            </p>
            <p className="mt-2 text-sm text-stone-500">
              <span className="font-semibold text-emerald-600">{t('Friday & Saturday')}</span>
              <span className="mx-2 text-stone-300">•</span>
              {t('Highest volume')}
            </p>

            <div className="mt-4 pt-4 border-t border-stone-200 grid grid-cols-3 gap-2 text-center">
              <div>
                <p className="text-[10px] uppercase tracking-widest font-bold text-stone-400">{t('AOV')}</p>
                <p className="text-sm font-bold text-stone-800 mt-1">${Number(data?.avg_order_value || 0).toFixed(2)}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest font-bold text-stone-400">{t('Visits')}</p>
                <p className="text-sm font-bold text-stone-800 mt-1">{Number(metrics.total_visits || 0).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest font-bold text-stone-400">{t('Customers')}</p>
                <p className="text-sm font-bold text-stone-800 mt-1">{Number(data?.total_customers || 0).toLocaleString()}</p>
              </div>
            </div>
            <button onClick={() => navigate('/merchant/ai')} className="mt-4 h-9 px-3 rounded-lg border border-brand/30 bg-brand-soft text-brand text-xs font-semibold">
              {t('Ask')} {assistantName}
            </button>
          </div>
          <div className="rounded-[30px] border border-stone-200/90 bg-[#fbfbfa] p-6 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-2xl bg-emerald-50 flex items-center justify-center">
                <Gift className="w-5 h-5 text-emerald-500" />
              </div>
              <div>
                <p className="text-base leading-none font-semibold text-stone-700 tracking-tight">{t('Top Redeemed Item')}</p>
                <p className="text-sm text-stone-400 mt-1">{t('Most popular reward')}</p>
              </div>
            </div>
            <div className="mt-6">
              <p className="text-lg leading-none font-bold tracking-tight text-[#0f172a]" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                {topRedeemedItemName}
              </p>
              <p className="mt-3 text-stone-500 text-sm">
                <span className="font-semibold text-stone-800">{Number(topRedeemedCount).toLocaleString()}</span>{' '}
                {t('redemptions')}
                <span className="mx-2 text-stone-300">•</span>
                <span className="font-medium text-stone-600">{Number(topRedeemedPoints).toLocaleString()} {t('pts')}</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="fixed bottom-5 right-5 z-40">
        <button
          type="button"
          onClick={() => navigate('/merchant/redemptions')}
          className="h-11 px-4 rounded-xl gradient-brand-btn text-sm font-semibold shadow-xl"
        >
          {t('Redeem Points')}
        </button>
      </div>

      <QrCodeModal open={showQR} onClose={() => setShowQR(false)} />
    </div>
  );
}
